import { CheckCheck, FlaskConical, Home, Tag, Truck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Dialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useLabActions, useLabOrder } from '@/features/orders/api';
import { CRITICAL_CONFIRM, computeFlag } from '@/features/orders/result-flag';
import {
  LAB_STATUS_LABEL,
  RESULT_FLAG_LABEL,
  type LabOrder,
  type LabResultFlag,
} from '@/features/orders/types';
import { LabelsDialog } from './LabelsDialog';
import { isApiError } from '@/lib/api';
import { LabResultsList } from '@/pages/clinical/LabResultsList';
import { PatientIdentityText } from '@/pages/patients/PatientIdentityText';
import styles from '@/pages/queue/queue.module.css';
import { CLINIC_TZ, fromClinicInput } from '@/lib/clinic-time';

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-GB', { timeZone: CLINIC_TZ }) : '—';

/** One lab order at the lab desk: move it on and enter results. */
export function LabOrderDrawer({
  orderId,
  onClose,
}: {
  orderId: string | null;
  onClose: () => void;
}) {
  const { data: o, isLoading, error, refetch } = useLabOrder(orderId);
  return (
    <Dialog
      open={!!orderId}
      onClose={onClose}
      title={o ? `${o.number} · ${o.patient.fullName}` : 'Lab order'}
    >
      {isLoading ? (
        <LoadingState />
      ) : error || !o ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <OrderBody key={`${o.id}:${o.version}`} order={o} />
      )}
    </Dialog>
  );
}

function OrderBody({ order: o }: { order: LabOrder }) {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const a = useLabActions();
  const toast = useToast();
  const can = hasPermission('lab_orders:process');
  const entering =
    can && (o.status === 'collected' || o.status === 'dispatched');
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
  const allResulted = o.tests.every((t) => t.result);
  const [labels, setLabels] = useState(false);
  const [homeAt, setHomeAt] = useState('');
  const [homePlace, setHomePlace] = useState('');

  return (
    <div className={styles.drawer}>
      <dl className={styles.rows}>
        <dt>
          {o.patient.identity?.type === 'passport' ? 'Passport' : 'MyKad'}
        </dt>
        <dd>
          <PatientIdentityText identity={o.patient.identity} />
        </dd>
        <dt>Status</dt>
        <dd>
          <Badge tone={o.status === 'results_ready' ? 'warning' : 'info'}>
            {LAB_STATUS_LABEL[o.status]}
          </Badge>
        </dd>
        <dt>Ordered</dt>
        <dd>
          {when(o.orderedAt)} ·{' '}
          {o.orderedBy?.fullName ?? t('common.notRecorded')}
        </dd>
        {o.clinicalNote && (
          <>
            <dt>Note</dt>
            <dd>{o.clinicalNote}</dd>
          </>
        )}
        {o.collectedAt && (
          <>
            <dt>Collected</dt>
            <dd>
              {when(o.collectedAt)}
              {o.collectedBy ? ` · ${o.collectedBy.fullName}` : ''}
            </dd>
          </>
        )}
        {o.dispatchedAt && (
          <>
            <dt>Dispatched</dt>
            <dd>{when(o.dispatchedAt)}</dd>
          </>
        )}
        {o.collectionMode === 'home' && (
          <>
            <dt>Home visit</dt>
            <dd>
              {when(o.homeVisitAt)} · {o.homeVisitPlace}
            </dd>
          </>
        )}
        <dt>Tubes</dt>
        <dd>
          {[
            ...new Set(o.tests.map((t) => t.tube ?? 'Plain / SST (gold)')),
          ].join(', ')}
          {o.labelsPrintedAt
            ? ` · labels printed ${when(o.labelsPrintedAt)}`
            : ''}
        </dd>
      </dl>

      {can && o.status === 'ordered' && o.collectionMode === 'clinic' && (
        <form
          className={styles.dxForm}
          aria-label="Book a home visit"
          onSubmit={(ev) => {
            ev.preventDefault();
            void a.homeVisit
              .mutateAsync({
                id: o.id,
                at: fromClinicInput(homeAt),
                place: homePlace.trim(),
                version: o.version,
              })
              .catch(fail);
          }}
        >
          <Input
            aria-label="Home visit time"
            type="datetime-local"
            value={homeAt}
            onChange={(ev) => setHomeAt(ev.target.value)}
          />
          <Input
            aria-label="Home visit place"
            placeholder="Area (address is in the record)"
            value={homePlace}
            onChange={(ev) => setHomePlace(ev.target.value)}
          />
          <Button
            type="submit"
            size="sm"
            icon={<Home size={14} />}
            disabled={!homeAt || homePlace.trim().length < 3}
            loading={a.homeVisit.isPending}
          >
            Home visit
          </Button>
        </form>
      )}

      {entering ? (
        <div className={styles.section}>
          <p className="eyebrow">Results</p>
          {o.tests.map((t) => (
            <ResultForm key={t.id} order={o} test={t} onError={fail} />
          ))}
        </div>
      ) : (
        <LabResultsList order={o} />
      )}

      {o.status === 'results_ready' && (
        <p className={styles.sub}>
          With {o.orderedBy?.fullName ?? t('common.notRecorded')} for review. A
          result is never released from the lab desk.
        </p>
      )}
      {o.reviewedAt && (
        <p className={styles.sub}>
          Reviewed {when(o.reviewedAt)}
          {o.reviewComment ? ` · ${o.reviewComment}` : ''}
        </p>
      )}

      {can && (
        <div className={styles.consultFooter}>
          {['ordered', 'collected', 'dispatched'].includes(o.status) && (
            <Button icon={<Tag size={16} />} onClick={() => setLabels(true)}>
              Labels
            </Button>
          )}
          {o.status === 'ordered' && (
            <Button
              variant="primary"
              icon={<FlaskConical size={16} />}
              loading={a.collect.isPending}
              onClick={() => a.collect.mutateAsync(o.id).catch(fail)}
            >
              Sample collected
            </Button>
          )}
          {o.status === 'collected' && (
            <Button
              icon={<Truck size={16} />}
              loading={a.dispatch.isPending}
              onClick={() => a.dispatch.mutateAsync(o.id).catch(fail)}
            >
              Dispatch to lab
            </Button>
          )}
          {entering && (
            <Button
              variant="primary"
              icon={<CheckCheck size={16} />}
              disabled={!allResulted}
              loading={a.resultsReady.isPending}
              onClick={() =>
                a.resultsReady
                  .mutateAsync(o.id)
                  .then(() =>
                    toast.show({
                      tone: 'success',
                      title: `Results sent to ${o.orderedBy?.fullName ?? t('common.notRecorded')}`,
                    }),
                  )
                  .catch(fail)
              }
            >
              Results ready
            </Button>
          )}
        </div>
      )}
      {labels && (
        <LabelsDialog orderId={o.id} onClose={() => setLabels(false)} />
      )}
    </div>
  );
}

const FLAG_OPTIONS = [
  { value: '', label: 'No flag' },
  ...(Object.keys(RESULT_FLAG_LABEL) as LabResultFlag[]).map((f) => ({
    value: f,
    label: RESULT_FLAG_LABEL[f],
  })),
];

function ResultForm({
  order,
  test,
  onError,
}: {
  order: LabOrder;
  test: LabOrder['tests'][number];
  onError: (err: unknown) => void;
}) {
  const a = useLabActions();
  const [value, setValue] = useState(test.result?.value ?? '');
  const [unit, setUnit] = useState(test.result?.unit ?? '');
  const [range, setRange] = useState(test.result?.referenceRange ?? '');
  const [flag, setFlag] = useState<string>(test.result?.flag ?? '');
  const [confirming, setConfirming] = useState(false);
  // RL-1843 (decision D17): the flag follows the range; Critical is the
  // lab's call and is confirmed before saving.
  const computed = computeFlag(value, range);
  const critical = flag === 'critical';
  const effective = critical ? 'critical' : (computed ?? flag);
  const save = (confirmCritical: boolean) =>
    void a.recordResult
      .mutateAsync({
        id: order.id,
        itemId: test.id,
        input: {
          value: value.trim(),
          unit: unit.trim() || undefined,
          referenceRange: range.trim() || undefined,
          flag: (effective || undefined) as LabResultFlag | undefined,
          ...(confirmCritical ? { confirmCritical: true } : {}),
        },
      })
      .then(() => setConfirming(false))
      .catch(onError);
  return (
    <form
      className={styles.section}
      aria-label={`Result for ${test.testName}`}
      onSubmit={(ev) => {
        ev.preventDefault();
        if (critical) setConfirming(true);
        else save(false);
      }}
    >
      <p className={styles.badges}>
        <strong>{test.testName}</strong>
        {test.result && <Badge tone="success">Saved</Badge>}
      </p>
      <div className={styles.vitalsGrid}>
        <Field label="Result">
          <Input value={value} onChange={(ev) => setValue(ev.target.value)} />
        </Field>
        <Field label="Unit">
          <Input value={unit} onChange={(ev) => setUnit(ev.target.value)} />
        </Field>
        <Field label="Range">
          <Input value={range} onChange={(ev) => setRange(ev.target.value)} />
        </Field>
        {computed ? (
          <Field
            label="Flag"
            hint={`${RESULT_FLAG_LABEL[computed]} from the range. Choose Critical if the doctor must call the patient.`}
          >
            <Select
              options={[
                { value: '', label: RESULT_FLAG_LABEL[computed] },
                { value: 'critical', label: RESULT_FLAG_LABEL.critical },
              ]}
              value={critical ? 'critical' : ''}
              onChange={(ev) => setFlag(ev.target.value)}
            />
          </Field>
        ) : (
          <Field label="Flag">
            <Select
              options={FLAG_OPTIONS}
              value={flag}
              onChange={(ev) => setFlag(ev.target.value)}
            />
          </Field>
        )}
      </div>
      {confirming && (
        <Dialog
          open
          variant="modal"
          title="Critical result"
          onClose={() => setConfirming(false)}
          footer={
            <>
              <Button onClick={() => setConfirming(false)}>Go back</Button>
              <Button
                variant="danger"
                loading={a.recordResult.isPending}
                onClick={() => save(true)}
              >
                Save as critical
              </Button>
            </>
          }
        >
          <p>{CRITICAL_CONFIRM}</p>
        </Dialog>
      )}
      <Button
        type="submit"
        size="sm"
        disabled={!value.trim()}
        loading={a.recordResult.isPending}
      >
        Save result
      </Button>
    </form>
  );
}
